import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  listCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  listProjects,
  listSeries,
  CALENDAR_STATUS_META,
  PLATFORMS,
  type ContentCalendarEvent,
  type ContentProject,
  type ContentSeries,
  type CalendarStatus,
} from '@/lib/contentStudio';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function toKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface DraftState {
  title: string;
  platform: string;
  scheduled_date: string;
  status: CalendarStatus;
  project_id: string;
  series_id: string;
  notes: string;
}

const EMPTY_DRAFT: DraftState = {
  title: '', platform: PLATFORMS[0], scheduled_date: toKey(new Date()),
  status: 'planned', project_id: '', series_id: '', notes: '',
};

/**
 * Calendrier de contenu : on planifie les publications (projet, plateforme,
 * date, statut) et on suit l'avancement des contenus.
 */
export default function ContentStudioCalendarPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState<ContentCalendarEvent[]>([]);
  const [projects, setProjects] = useState<ContentProject[]>([]);
  const [series, setSeries] = useState<ContentSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [statusFilter, setStatusFilter] = useState<'all' | CalendarStatus>('all');
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [evts, projs, srs] = await Promise.all([
        listCalendarEvents(user.id),
        listProjects(user.id),
        listSeries(user.id),
      ]);
      setEvents(evts);
      setProjects(projs);
      setSeries(srs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const resetDraft = () => { setDraft({ ...EMPTY_DRAFT, scheduled_date: toKey(new Date()) }); setEditingId(null); };

  const openEdit = (ev: ContentCalendarEvent) => {
    setEditingId(ev.id);
    setDraft({
      title: ev.title,
      platform: ev.platform || PLATFORMS[0],
      scheduled_date: ev.scheduled_date || toKey(new Date()),
      status: (ev.status as CalendarStatus) || 'planned',
      project_id: ev.project_id ? String(ev.project_id) : '',
      series_id: ev.series_id ? String(ev.series_id) : '',
      notes: ev.notes || '',
    });
  };

  const handleSave = async () => {
    if (!user?.id || !draft.title.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: draft.title.trim().slice(0, 140),
        platform: draft.platform,
        scheduled_date: draft.scheduled_date || null,
        status: draft.status,
        project_id: draft.project_id ? Number(draft.project_id) : null,
        series_id: draft.series_id ? Number(draft.series_id) : null,
        notes: draft.notes || null,
      };
      if (editingId) await updateCalendarEvent(editingId, payload);
      else await createCalendarEvent(user.id, payload);
      resetDraft();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur d\u2019enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCalendarEvent(id);
      if (editingId === id) resetDraft();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de suppression');
    }
  };

  const filtered = useMemo(
    () => events.filter((e) => statusFilter === 'all' || e.status === statusFilter),
    [events, statusFilter],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, ContentCalendarEvent[]>();
    filtered.forEach((e) => {
      if (!e.scheduled_date) return;
      const key = e.scheduled_date.slice(0, 10);
      const arr = map.get(key) || [];
      arr.push(e);
      map.set(key, arr);
    });
    return map;
  }, [filtered]);

  // Grille du mois (commence lundi)
  const gridCells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < offset; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const upcoming = useMemo(
    () => filtered.filter((e) => e.scheduled_date).slice().sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || '')),
    [filtered],
  );

  const projectName = (id: number | null) => projects.find((p) => p.id === id)?.name || null;
  const todayKey = toKey(new Date());

  if (!user?.id) {
    return <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <Link to="/dashboard/content-studio" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-4 cursor-pointer">
        <i className="ri-arrow-left-line"></i>Content Studio
      </Link>

      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-calendar-2-line text-accent-500"></i>Calendrier de contenu
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Planifiez vos publications par date et par plateforme, et suivez leur avancement.</p>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Calendrier */}
        <div className="xl:col-span-2">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <h3 className="text-sm font-bold text-foreground-900">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
              <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map((w) => <div key={w} className="text-[10px] font-semibold text-foreground-400 text-center uppercase tracking-wide">{w}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {gridCells.map((cell, i) => {
                if (!cell) return <div key={`empty-${i}`} className="min-h-[74px] rounded-md bg-background-100/40"></div>;
                const key = toKey(cell);
                const dayEvents = byDay.get(key) || [];
                const isToday = key === todayKey;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setDraft((d) => ({ ...d, scheduled_date: key })); setEditingId(null); }}
                    className={`min-h-[74px] rounded-md border p-1.5 text-left transition-colors cursor-pointer ${isToday ? 'border-accent-300 bg-accent-50/50' : 'border-background-200/60 bg-background-50 hover:bg-background-100/60'}`}
                  >
                    <span className={`text-[11px] font-semibold ${isToday ? 'text-accent-700' : 'text-foreground-500'}`}>{cell.getDate()}</span>
                    <div className="mt-1 space-y-0.5">
                      {dayEvents.slice(0, 2).map((e) => (
                        <span key={e.id} className="block text-[9px] leading-tight px-1 py-0.5 rounded bg-primary-100 text-primary-800 truncate">{e.title}</span>
                      ))}
                      {dayEvents.length > 2 && <span className="block text-[9px] text-foreground-400">+{dayEvents.length - 2}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filtre statut */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mt-4 w-fit flex-wrap">
            {([['all', 'Tous'], ['planned', 'Prévu'], ['in_progress', 'En cours'], ['published', 'Publié']] as const).map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setStatusFilter(val as 'all' | CalendarStatus)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${statusFilter === val ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Formulaire + liste */}
        <div className="space-y-5">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <h3 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
              <i className={`${editingId ? 'ri-edit-line' : 'ri-add-line'} text-foreground-500`}></i>
              {editingId ? 'Modifier la planification' : 'Planifier un contenu'}
            </h3>
            <div className="space-y-3">
              <input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} placeholder="Titre du contenu" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300" />
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <select value={draft.platform} onChange={(e) => setDraft((d) => ({ ...d, platform: e.target.value }))} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer">
                    {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                </div>
                <input type="date" value={draft.scheduled_date} onChange={(e) => setDraft((d) => ({ ...d, scheduled_date: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <select value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as CalendarStatus }))} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer">
                    <option value="planned">Prévu</option>
                    <option value="in_progress">En cours</option>
                    <option value="published">Publié</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                </div>
                <div className="relative">
                  <select value={draft.project_id} onChange={(e) => setDraft((d) => ({ ...d, project_id: e.target.value }))} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer">
                    <option value="">Projet…</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                </div>
              </div>
              <textarea value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} rows={2} placeholder="Notes (optionnel)" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-accent-300 resize-none" />
              <div className="flex items-center gap-2">
                {editingId && (
                  <button type="button" onClick={resetDraft} className="flex-1 py-2 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
                )}
                <button type="button" onClick={handleSave} disabled={!draft.title.trim() || saving} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer">
                  {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                  {editingId ? 'Mettre à jour' : 'Planifier'}
                </button>
              </div>
            </div>
          </div>

          {/* Liste à venir */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <h3 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-list-check-2 text-foreground-500"></i>Planning
              <span className="text-xs font-normal text-foreground-400">({upcoming.length})</span>
            </h3>
            {loading ? (
              <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-lg text-accent-500"></i></div>
            ) : upcoming.length === 0 ? (
              <p className="text-xs text-foreground-400 py-4 text-center">Aucun contenu planifié.</p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {upcoming.map((e) => {
                  const meta = CALENDAR_STATUS_META[(e.status as CalendarStatus)] || CALENDAR_STATUS_META.planned;
                  return (
                    <div key={e.id} className="flex items-start gap-2 p-2.5 rounded-lg border border-background-200/70">
                      <div className="w-10 flex-shrink-0 text-center">
                        <p className="text-[10px] font-semibold text-foreground-400 uppercase">{e.scheduled_date ? MONTHS[new Date(e.scheduled_date).getMonth()].slice(0, 3) : '—'}</p>
                        <p className="text-sm font-bold text-foreground-800 leading-none">{e.scheduled_date ? new Date(e.scheduled_date).getDate() : '—'}</p>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground-800 truncate">{e.title}</p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {e.platform && <span className="text-[10px] text-foreground-500 inline-flex items-center gap-0.5"><i className="ri-live-line"></i>{e.platform}</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.className}`}>{meta.label}</span>
                          {e.media_url && <span className="text-[10px] text-primary-700 inline-flex items-center gap-0.5 bg-primary-100 px-1.5 py-0.5 rounded-full"><i className="ri-film-line"></i>Vidéo</span>}
                          {projectName(e.project_id) && <span className="text-[10px] text-foreground-400 truncate max-w-[110px]">{projectName(e.project_id)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {e.media_url && (
                          <a
                            href={e.media_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                            title="Ouvrir le média"
                          >
                            <i className="ri-play-circle-line text-sm"></i>
                          </a>
                        )}
                        <button type="button" onClick={() => openEdit(e)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer" title="Modifier"><i className="ri-edit-line text-sm"></i></button>
                        <button type="button" onClick={() => handleDelete(e.id)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Supprimer"><i className="ri-delete-bin-line text-sm"></i></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}